<template>
	<li class="file-edit-item">
		<div class="relative">
			<div
				v-if="uploadState?.state === 'uploading'"
				class="absolute top-1/2 left-1/2 -translate-1/2 z-10"
			>
				<AppProgress
					:progress="(uploadState?.progress ?? 0) * 100"
					:stroke-width="4"
					:size="24"
					progress-color="#fff"
					background-color="#999"
					class="absolute"
				/>
			</div>
			<FilePreviewFileBox
				:class="{ 'brightness-50': uploadState?.state === 'uploading' }"
				mode="thumbnail"
				:file-blob="fileMetadata.file"
			/>
		</div>
		<div class="file-editor">
			<AppButton
				variant="ghost"
				icon="cancel"
				aria-label="Verwijder bestand"
				class="remove-button"
				:disabled="uploadState?.state === 'uploading'"
				@click="onRemove"
			/>
			<input
				class="filename"
				:value="fileMetadata.newName"
				:disabled="uploadState?.state === 'uploading'"
				@input="onInput($event as InputEvent)"
			/>
			<span v-if="extension?.length > 0" class="file-extension">
				.{{ extension }}
			</span>
		</div>
	</li>
</template>

<script lang="ts" setup>
import type { FileMetadata } from "@/models/FileMetadata";
import { useFileUploadStore } from "@/store/useFileUploadStore";
import { useGlobalUploadStore } from "@/store/useGlobalUploadStore";
import { computed, ref } from "vue";

const props = defineProps<{
	fileMetadata: FileMetadata;
}>();

const emit = defineEmits<{
	"update:fileMetadata": [value: string];
	"remove:file": [];
}>();

const fileUploadStore = useFileUploadStore();
const globalUploadStore = useGlobalUploadStore();

const currentDocument = computed(() => {
	const doc = fileUploadStore.filesToDocumentId.find(
		(doc) => doc.file === props.fileMetadata.file,
	);

	return doc;
});

const uploadState = computed(() => {
	return globalUploadStore.getUploadState(
		currentDocument.value?.documentId ?? null,
	);
});

function onInput(e: InputEvent) {
	emit("update:fileMetadata", (e.target as HTMLInputElement).value);
}

function onRemove() {
	emit("remove:file");
}

const extension = ref(props.fileMetadata.file.name.split(".").pop() ?? "");
</script>

<style lang="scss">
.file-edit-item {
	display: flex;
	flex-direction: row;
	gap: 20px;
	width: 100%;
	padding: 12px 20px;
	border-radius: var(--border-radius);
	border: 1px solid var(--border);
	background: white;

	.file-preview {
		height: 200px;
		width: 200px;
		display: flex;
		align-items: center;
		justify-content: center;

		.file-preview-image {
			object-fit: contain;
		}
	}

	.file-editor {
		display: flex;
		flex-direction: row;
		column-gap: 6px;
		align-items: center;
		flex-grow: 1;
		position: relative;

		.remove-button {
			position: absolute;
			right: -8px;
			top: 0;
			color: var(--color-fail);
			cursor: pointer;
			font-size: 1.5rem;

			&:hover {
				color: var(--color-fail-darker);
			}
		}

		.filename {
			flex-grow: 1;
			border: 1px solid var(--border);
			border-radius: var(--border-radius);
			padding: 7px 12px;
		}

		.file-extension {
			flex-grow: 0;
			color: var(--text-muted);
			font-weight: 600;
			font-size: 0.85rem;
		}
	}
}
</style>
